/**
 * The default working state — a :type:`ProfileDocument` applied when the
 * BFF returns an empty preferences document (no ``auth.user_preferences``
 * row) and no built-in default Global Profile is available to auto-apply,
 * and as the base the v3 ``buildPutBody`` writes from (ADR-033 §3.1 / §3.2).
 *
 * The shape mirrors the BFF's ``ProfileDocumentV1`` field defaults. We keep
 * it deliberately **minimal** — only the slices the UI consumes today
 * (theme + layout + entity-detail platform sections + the dynamic-track map
 * setting) are populated; the rest (units, clocks, full map glyph/colour,
 * the other entity types) ride as the document's own defaults, filled by
 * later increments. ``theme`` is ``"dark"`` to match the deployed default
 * (design-system.md 2026-05-26 amendment; ADR-033 D10).
 *
 * This is **not** a substitute for the built-in default Global Profile
 * (which the BFF seeds and the UI auto-applies on first sign-in per D9) —
 * it is the local fallback for the degenerate case where no profile is
 * available (e.g., a bootstrap that predates the ``profiles`` field).
 *
 * Cross-references:
 *   - REQ-AUTH-070 (v3 working_state is a Profile document).
 *   - REQ-UI-093 (first-run auto-apply — the profile path is preferred;
 *     this is the no-profile fallback).
 *   - ADR-033 §3.1 (Profile document shape), §3.2 (working state), D10.
 */
import type { ProfileDocument } from "@/auth/session-types";

/**
 * Build a fresh default working-state document. Returns a new object each
 * call so callers can mutate their copy freely.
 */
export function buildDefaultWorkingState(): ProfileDocument {
  return {
    profile_schema_version: 1,
    theme: "dark",
  };
}
